/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractSnapshotFormComponent,
  DateTimeTypes,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  toAppFormGroup,
  updateChildrenSchema,
} from '@gh/common-shared';
import { SelectionModel, SlicedDataSource } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import {
  AutocompleteLoadMoreEvent,
  getControlValue,
  setControlValue,
  STYLES_FLEX_COLUMN_LAYOUT,
} from '@gh/core-ui';
import { GridDefinition, GridDefinitionFactory } from '@gh/core-ui-grid';
import { isDateValid, isNotNil } from '@gh/core-util';
import { ScheduleListBusinessService } from '../../../resources/schedule-list-business.service';
import {
  ScheduleSupervisorReportFilter,
  ScheduleSupervisorReportTask,
} from '../../../resources/schedule-list.model';

const getFormSchemaChildrenConfig = (filter: ScheduleSupervisorReportFilter) => ({
  startDate: {
    required: true,
    type: DateTimeTypes.ShortDate,
  },
  endDate: {
    required: true,
    type: DateTimeTypes.ShortDate,
  },
  supervisor: { required: true, enabled: !filter.isAllSupervisors },
  showUnproduced: void 0,
  showZeroCost: void 0,
  isAllSupervisors: void 0,
  orderBy: void 0,
  showCriticalPathDescription: void 0,
  isNewPageForStartDate: { visible: filter.orderBy === 'startDate' },
  isNewPageForEachActivity: { visible: filter.orderBy === 'costCenterCode' },
});

@Component({
  standalone: false,
  selector: 'gh-schedule-supervisors-report-filter-task-step',
  templateUrl: 'schedule-supervisors-report-filter-task-step.component.html',
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleSupervisorsReportFilterTaskStepComponent extends AbstractSnapshotFormComponent<ScheduleSupervisorReportFilter> implements OnInit {
  @Input() selectionModel: SelectionModel;
  @Input() dataSource: SlicedDataSource<ScheduleSupervisorReportTask>;

  @Output() selectionModelChange = new EventEmitter<SelectionModel>();
  @Output() taskExistChange = new EventEmitter<boolean>();

  gridDefinition: GridDefinition;

  constructor(private translations: TranslationService,
              private formStateBuilder: FormStateBuilder,
              private gridDefinitionFactory: GridDefinitionFactory,
              private business: ScheduleListBusinessService) {
    super();

    this.isStartDateAllowed = this.isStartDateAllowed.bind(this);
    this.isEndDateAllowed = this.isEndDateAllowed.bind(this);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.gridDefinition = this.gridDefinitionFactory.buildManual(
      [
        { field: 'parentName', headerName: this.translations.get('lbl_task'), width: 200 },
        { field: 'name', headerName: this.translations.get('lbl_description'), width: 400 },
      ],
      {
        getRowId: (params) => String(params.data.scheduleTaskId),
        defaultColDef: {
          sortable: false,
        },
      });
  }

  loadMoreSupervisors(event: AutocompleteLoadMoreEvent): void {
    event.load(this.business.findAllSupervisors(event.query));
  }

  isStartDateAllowed(date: Date): boolean {
    const endDate = getControlValue(this.form.group, 'endDate');
    return isNotNil(endDate) && isDateValid(endDate) ? date <= endDate : true;
  }

  isEndDateAllowed(date: Date): boolean {
    const startDate = getControlValue(this.form.group, 'startDate');
    return isNotNil(startDate) && isDateValid(startDate) ? date >= startDate : true;
  }

  onAllSupervisorsChange(): void {
    const isAllSupervisors = getControlValue(this.form.group, 'isAllSupervisors');
    if (isAllSupervisors) {
      setControlValue(this.form.group, 'orderBy', 'costCenterCode');
    }
  }

  onOrderByChange(): void {
    const orderBy = getControlValue(this.form.group, 'orderBy');
    if (orderBy === 'startDate') {
      setControlValue(this.form.group, 'isAllSupervisors', false);
    }
  }

  protected buildForm(snapshot: FormStateSnapshot<ScheduleSupervisorReportFilter>): FormState<ScheduleSupervisorReportFilter> {
    return this.formStateBuilder.buildPlain({
      snapshot,
      children: getFormSchemaChildrenConfig(getFormStateSnapshotValue(snapshot)),
    });
  }

  protected afterRestored(current: FormStateSnapshot<ScheduleSupervisorReportFilter>): void {
    updateChildrenSchema(
      toAppFormGroup(this.form.group),
      getFormSchemaChildrenConfig(getFormStateSnapshotValue(current)));
  }
}
